import { showBootstrapTab } from 'ws-app'
import { validateRequiredInput, validateMinimum } from 'ws-validation'

class DesignDialogComponent extends HTMLElement {

  constructor() {
    super();
    document.addEventListener('project-loaded', (event) => this.configure(event.detail))
    document.addEventListener('shape-or-material-changed', (event) => this.configure(event.detail))
    document.addEventListener('use-load-combo-did-change', (event) => this.configureLoadCombinationTable(event.detail))
    document.addEventListener('use-lambda-or-cd-did-change', (event) => this.configureLoadCombinationTable(event.detail))
    document.addEventListener('design-type-did-change', (event) => this.onDesignTypeDidChange(event.detail))
  }

  setActiveButton(project) {
    if(project.isWood || project.isNorthAmericanSteel || project.isEngineeredLumber) {
      if(project.isLRFD) {
        showBootstrapTab('lrfd-combinations-button')
      } else if(project.isASD) {
        showBootstrapTab('asd-combinations-button')
      } 
    } else {
      showBootstrapTab('no-combinations-button')
    }
  }

  configure(project) {
    document.getElementById('modal-design-dialog-label').innerHTML = `Design Specification - ${project.designTypeStr}`
    this.setActiveButton(project)
    if(project.isWood || project.isNorthAmericanSteel || project.isEngineeredLumber) {
        document.getElementById('design-available-warning').classList.add('d-none')
        document.getElementById('lrfd-combinations-button').classList.remove('d-none')
        document.getElementById('asd-combinations-button').classList.remove('d-none')
        if(project.isEngineeredLumber) {
          document.getElementById('design-available-warning').classList.remove('d-none')
          document.getElementById('design-dialog-warning-text').innerHTML = `
          <div class="alert alert-info" role="alert">
            <strong>Note:</strong> Design checks based on manufacturer's design capacity for ${project.material['Type']} shapes.
          </div>`
        }
    } else {
        document.getElementById('design-available-warning').classList.remove('d-none')
        document.getElementById('lrfd-combinations-button').classList.add('d-none')
        document.getElementById('asd-combinations-button').classList.add('d-none')
        document.getElementById('design-dialog-warning-text').innerHTML = `
          <div class="alert alert-info" role="alert">
            <strong>Note:</strong> Only Basic Design is available for ${project.material['Type']} shapes.
          </div>`
    }

    this.configureDeflectionLimits(project)
    this.configureLoadCombinationTableHeader(project)
    this.configureLoadCombinationTable(project)
  }

  onDesignTypeDidChange(project) {
    document.getElementById('modal-design-dialog-label').innerHTML = `Design Specification - ${project.designTypeStr}`
    this.configureDeflectionLimits(project)
    this.configureLoadCombinationTableHeader(project)
    this.configureLoadCombinationTable(project)
  }

  connectedCallback() {
    this.innerHTML = String.raw`
    <div class="modal fade" id="modal-design-dialog" tabindex="-1" role="dialog">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header border-bottom-0">
            <h1 class="modal-title fs-5" id="modal-design-dialog-label">Design Specifications</h1>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>

          <div class="modal-body modal-body-shadow">
            <div class="panel panel-warning d-none" id="design-available-warning">
              <div class="panel-heading" id="design-dialog-warning-text"></div>
            </div>

            <div class="text-center d-flex justify-content-center">
              
              <ul class="nav nav-pills selector-nav-pills mb-3" role="tablist">
                <li class="nav-item px-1" role="presentation">
                  <button type="button" role="tab" class="nav-link selector-nav-link active show" id="lrfd-combinations-button" data-bs-toggle="tab">
                    LRFD
                  </button>
                </li>
                <li class="nav-item px-1" role="presentation">
                  <button type="button" role="tab" class="nav-link selector-nav-link" id="asd-combinations-button" data-bs-toggle="tab">
                    ASD
                  </button>
                </li>
                <li class="nav-item px-1" role="presentation">
                  <button type="button" role="tab" class="nav-link selector-nav-link" id="no-combinations-button" data-bs-toggle="tab">
                    Basic Design
                  </button>
                </li>
              </ul>
            </div>
          </div>

          <div class="modal-body">
            <div class="card my-2">
              <div class="card-body">
                <table class="table table-borderless">
                  <thead>
                    <tr>
                      <th colspan=3>Deflection Limits</th>
                    </tr>
                  </thead>
                  <tr>
                    <td>Live Load Deflection Limit (LL):</td>
                    <td>
                      L / <input style='width:100px;' id='live-load-deflection-limit-input' type='number' value='360' />
                    </td>
                    <td><i>L + (Lr or S)</i></td>
                  </tr>
                  <tr>
                    <td>Dead Load + Live Limit:</td>
                    <td>
                      L / <input style='width:100px;' id='dead-plus-live-load-deflection-limit-input' type='number' value='360' />
                    </td>
                    <td><i>D+L</i></td>
                  </tr>
                  <tr>
                    <td>Snow or Wind Limit:</td>
                    <td>
                      L / <input style='width:100px;' id='snow-or-wind-load-deflection-limit-input' type='number' value='360' />
                    </td>
                    <td><i>S or W</i></td>
                  </tr>
                  <tr>
                    <td>Total Load Deflection Limit (TL):</td>
                    <td>
                      L / <input style='width:100px;' id='total-load-deflection-limit-input' type='number' value='240' />
                    </td>
                    <td><i>D+L+S+R+W+H</i></td>
                  </tr>
                </table>
              </div>
            </div>
            <div class="card">
              
              <div class="m-2">

                <span>
                  <input class="form-check-input" type="checkbox" id="use-load-combo-equations">
                </span>
                <span id="load-combination-label"></span>
                <span>
                  <i class="fa fa-question-circle text-blue" data-bs-toggle="tooltip" data-bs-placement="right" 
                    title="Click individual load cases below to exclude them from your design.">
                  </i>
                </span>

                <span class="mx-4" id="lambda-or-cd-toggle">
                  <span>
                    <input class="form-check-input" type="checkbox" value="" id="use-lambda-or-cd">
                  </span>
                  <span id="lambda-or-cd-label">Use &lambda;</span>
                  <i class="fa fa-question-circle text-blue" data-bs-toggle="tooltip" data-bs-placement="right" 
                  title="Load duration factor. Account for wood's ability to carry higher loads for very short durations.">
                  </i>
                </span>

                <span class="mx-4" id="repetitive-member-factor-toggle">
                  <span>
                    <input class="form-check-input" type="checkbox" value="" id="use-repetitive-member-factor">
                  </span>
                  <span>Cr, repetitive member factor</span>
                  <i class="fa fa-question-circle text-blue" data-bs-toggle="tooltip" data-bs-placement="right" 
                    
                    title="Increase capacity by 15%. Valid when three or more parallel members of dimension lumber are spaced not more than 24 in. o.c. 
                    Members must be connected together by a load-distributing element such as roof, floor, or wall sheathing">
                  </i>
                </span>
              </div>

              <div class="card-body load-combination-body">
                <div id="load-combination-table"></div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
          </div>
        </div>
      </div>
    </div>
    `

    document.getElementById('live-load-deflection-limit-input').onchange = () => onDeflectionLimitChanged()
    document.getElementById('dead-plus-live-load-deflection-limit-input').onchange = () => onDeflectionLimitChanged()
    document.getElementById('snow-or-wind-load-deflection-limit-input').onchange = () => onDeflectionLimitChanged()
    document.getElementById('total-load-deflection-limit-input').onchange = () => onDeflectionLimitChanged()
  }

  configureDeflectionLimits(project) {
    document.getElementById('live-load-deflection-limit-input').value = project.design.liveLoadLimit
    document.getElementById('dead-plus-live-load-deflection-limit-input').value = project.design.deadPlusLiveLoadLimit
    document.getElementById('snow-or-wind-load-deflection-limit-input').value = project.design.snowOrWindLoadLimit
    document.getElementById('total-load-deflection-limit-input').value = project.design.totalLoadLimit
  }

  configureLoadCombinationTableHeader(project) {
    const design = project.design

    if (design.designType === 'None') {
        document.getElementById('use-load-combo-equations').classList.add('d-none')
        document.getElementById('load-combination-label').innerHTML = 'None'
        document.getElementById('lambda-or-cd-toggle').classList.add('d-none')
        document.getElementById('repetitive-member-factor-toggle').classList.add('d-none')
        return
    }

    let useEquations = design.loadCombination.name !== 'No Combos'

    document.getElementById('use-load-combo-equations').classList.remove('d-none')
    document.getElementById('use-load-combo-equations').checked = useEquations
    document.getElementById('load-combination-label').innerHTML = `Use ${design.designType} Equations`
    
    let showLambdaOrCd = (project.isWood || project.isEngineeredLumber) && (project.isLRFD || project.isASD)

    if(showLambdaOrCd) {
        document.getElementById('lambda-or-cd-label').innerHTML = project.isLRFD ? "Include &lambda;" : "Include C<sub>D</sub>"
        document.getElementById('lambda-or-cd-toggle').classList.remove('d-none')
        document.getElementById('use-lambda-or-cd').checked = project.design.useLambdaOrCd
    } else {
        document.getElementById('lambda-or-cd-toggle').classList.add('d-none')
    }

    let showRepetitiveMemberFactor = (project.isWood) && (project.isLRFD || project.isASD)

    if(showRepetitiveMemberFactor) {
      document.getElementById('repetitive-member-factor-toggle').classList.remove('d-none')
      document.getElementById('use-repetitive-member-factor').checked = project.design.useRepetitiveMemberFactor
    } else {
        document.getElementById('repetitive-member-factor-toggle').classList.add('d-none')
    }
  }

  lambdaOrCdString(project){
      if(!project.isWood && !project.isEngineeredLumber) {
        return ""
      }

      const useLambda = document.getElementById('use-lambda-or-cd') ? document.getElementById('use-lambda-or-cd').checked : false
      if(!useLambda) {
        return ""
      }

      if(project.isLRFD) {
        return "&lambda; (Time Effect Factor)"
          
      } 
      
      if(project.isASD) {
        return "C<sub>D</sub> (Load Duration Factor)"
      }
      return ""
  }

  configureLambdaOrCdFields(project) {
    if(!project.isWood && !project.isEngineeredLumber) {
        return
    }
    let useLambda = document.getElementById('use-lambda-or-cd') ? document.getElementById('use-lambda-or-cd').checked : false

    useLambda = useLambda && project.design.designType !== 'None'
    project.design.useLambdaOrCd = useLambda

    useLambda ? document.getElementById('lambda-or-cd-header-label').classList.remove('d-none') : document.getElementById('lambda-or-cd-header-label').classList.add('d-none')
    document.getElementById('lambda-or-cd-header-label').innerHTML = this.lambdaOrCdString(project)
    
    for( var i = 0; i < project.design.loadCombination.prettyEquations.length; i++ ) {
      if(project.design.loadCombination.prettyEquations[i]["common"] === false) {
          continue;
      }
      if(useLambda) {
          document.getElementById(`lambda-or-cd-${i}`).classList.remove('d-none')
      } else {
          document.getElementById(`lambda-or-cd-${i}`).classList.add('d-none')
      }
    }
  }

  updateLambdaOrCd(event) {
    const equationName = `EQ${(Number(event.target.id.split('lambda-or-cd-')[1]) + 1)}`
    const value = Number(event.target.value)
    document.dispatchEvent(new CustomEvent('lambda-or-cd-changed', {detail: {equationName: equationName, value: value}}))
  }

  configureLoadCombinationTable(project) {
    // Show nicely formatted Equations
    const design = project.design;
    if (design.loadCombination === null) { return }

    let str = '<table class="table table-borderless table-hover">'

    let useEquations = design.loadCombination.name !== 'No Combos'
    //let useEquations = (design.loadCombination !== null && design.loadCombination.name !== 'No Combos')
    let comboLabel = design.designType === 'None' || useEquations === false ? "Load Case" : "Combination"
    str += `<tr style="padding: 6px;"><th>${comboLabel}</th><th><span id="lambda-or-cd-header-label">${this.lambdaOrCdString(project)}</span></th></tr>`

    for( let i = 0; i < design.loadCombination.prettyEquations.length; i++ ) {
      if(design.loadCombination.prettyEquations[i]["common"] === false ) {
          continue;
      }
      const equationName = `EQ${i+1}`
      str += String.raw`
        <tr style="padding: 6px;" class="${design.loadCombination.prettyExclude[i] ? 'text-muted' : ''}" id="load-combination-row-${i}">
          <td style="padding: 6px;" id="load-combination-column-${i}">
            <span id="load-combination-check-${i}" class="${design.loadCombination.prettyExclude[i] ? '' : 'fa fa-check'}"> </span>
            ${design.loadCombination.prettyEquations[i]["EQ"]}</td><td style="padding: 6px;">
            <input class='${project.isWood || project.isEngineeredLumber ? '' : 'd-none'}' id='lambda-or-cd-${i}' type='text' 
              value='${design.lambdaOrCdValue(equationName)}'/>
          </td>
        </tr>`
    }
    str += "</table>"
    document.getElementById('load-combination-table').innerHTML = str

    for( let i = 0; i < design.loadCombination.prettyEquations.length; i++ ) {
      if(design.loadCombination.prettyEquations[i]["common"] === false ) {
          continue;
      }
      document.getElementById(`lambda-or-cd-${i}`).onchange = (event) => this.updateLambdaOrCd(event)
      document.getElementById(`load-combination-column-${i}`).onclick = () => toggleLoadCombinationEquation(i)
    }

    this.configureLambdaOrCdFields(project)
  }

}

customElements.define('design-dialog-component', DesignDialogComponent)

function toggleLoadCombinationEquation(index) {
  document.getElementById(`load-combination-check-${index}`).classList.toggle('fa')
  document.getElementById(`load-combination-check-${index}`).classList.toggle('fa-check')
  document.getElementById(`load-combination-row-${index}`).classList.toggle('text-muted')
  document.dispatchEvent(new CustomEvent('toggle-load-combination-equation', {detail: index}))
}

function onDeflectionLimitChanged() {
  let designLimits = {}
  let id = 'live-load-deflection-limit-input'
  if(validateRequiredInput(id) && validateMinimum(id, 1)) {
    designLimits.liveLoadLimit = parseInt(document.getElementById(id).value)
  } else { 
      return
  }
  id = 'dead-plus-live-load-deflection-limit-input'
  if(validateRequiredInput(id) && validateMinimum(id, 1)) {
    designLimits.deadPlusLiveLoadLimit = parseInt(document.getElementById(id).value)
  } else { 
      return
  }
  id = 'snow-or-wind-load-deflection-limit-input'
  if(validateRequiredInput(id) && validateMinimum(id ,1)) {
    designLimits.snowOrWindLoadLimit = parseInt(document.getElementById(id).value)
  } else { 
      return
  }
  id = 'total-load-deflection-limit-input'
  if(validateRequiredInput(id) && validateMinimum(id, 1)) {
    designLimits.totalLoadLimit = parseInt(document.getElementById(id).value)
  } else { 
      return
  }
  document.dispatchEvent(new CustomEvent('design-deflection-limits-changed', {detail: designLimits}))
}